import { useState, useEffect } from 'react';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon,
  Clock,
  Filter,
  Download,
  Fingerprint,
  Users,
  AlertCircle,
  BarChart3,
  TrendingUp,
  Wallet,
  IndianRupee,
  Timer,
  CheckCircle2,
  Award,
  TrendingDown,
  Sparkles,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { AttendanceStats, Staff, staffService, attendanceService } from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const AdminAttendanceAnalyticsPage = () => {
  const { toast } = useToast();
  const getLocalDateString = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  
  // Month-wise vs Custom Range selector
  const [periodType, setPeriodType] = useState<'month' | 'custom'>('month');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [customStartDate, setCustomStartDate] = useState(() => {
    const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
    return `${todayStr.slice(0, 8)}01`;
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  });

  // Selected calendar day detail state
  const [selectedCalDate, setSelectedCalDate] = useState<string | null>(null);

  // Generate last 6 months options dynamically
  const monthOptions = (() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      options.push({ label, value });
    }
    return options;
  })();

  // Animation config
  const containerVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35, staggerChildren: 0.06 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.25 } }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  useEffect(() => {
    loadStats();
    setSelectedCalDate(null); // Reset calendar selection on filter change
    const interval = setInterval(loadStats, 10000);
    return () => clearInterval(interval);
  }, [periodType, selectedMonth, selectedStaffId, customStartDate, customEndDate]);

  const loadStaff = async () => {
    try {
      const data = await staffService.getAll();
      setStaffList(data);
    } catch (e) {
      toast({ title: 'Failed to load staff list', variant: 'destructive' });
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const sid = selectedStaffId === 'all' ? undefined : selectedStaffId;
      let start = '';
      let end = '';

      if (periodType === 'month') {
        const [year, month] = selectedMonth.split('-').map(Number);
        start = `${selectedMonth}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        end = `${selectedMonth}-${String(lastDay).padStart(2, '0')}`;
      } else {
        start = customStartDate;
        end = customEndDate;
      }

      const data = await attendanceService.getStats('custom', sid, start, end);
      setStats(data);
    } catch (e) {
      console.error('Failed to load attendance analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatHM = (minutes: number) => {
    const h = Math.floor(Math.abs(minutes) / 60);
    const m = Math.round(Math.abs(minutes) % 60);
    return `${String(h).padStart(2, '0')}H ${String(m).padStart(2, '0')}M`;
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!stats) return;
    try {
      let csvContent = "data:text/csv;charset=utf-8,";
      
      if (selectedStaffId === 'all') {
        csvContent += "Staff Name,On Time Days,Late Days,Absent Days,Total Late Minutes,Days Worked,Total Hours\n";
        stats.staffSummary.forEach(s => {
          csvContent += `"${s.name}",${s.present},${s.late},${s.absent},${s.lateMinutes},${s.daysWorked},${s.totalHours.toFixed(2)}\n`;
        });
      } else if (stats.staffDetail) {
        const detail = stats.staffDetail;
        csvContent += `Attendance Report for ${detail.name}\n`;
        csvContent += `Period: ${periodType === 'month' ? selectedMonth : `${customStartDate} to ${customEndDate}`}\n\n`;
        csvContent += `Metric,Value\n`;
        csvContent += `Days On Time,${detail.totalPresent}\n`;
        csvContent += `Days Late,${detail.totalLate}\n`;
        csvContent += `Total Late Time,${formatHM(detail.totalLateMinutes)}\n`;
        csvContent += `Absent Days,${detail.totalAbsent}\n`;
        csvContent += `Wallet Balance,₹${detail.walletBalance}\n`;
        csvContent += `Monthly Wages,₹${detail.totalWages}\n`;
        csvContent += `Total Hours Worked,${detail.totalWorkingHours.toFixed(2)}\n`;
        csvContent += `Average Daily Hours,${detail.avgWorkingHours.toFixed(2)}\n\n`;

        if (detail.lateDetails && detail.lateDetails.length > 0) {
          csvContent += `Late Arrival Date,Check-in Time,Minutes Late\n`;
          detail.lateDetails.forEach(ld => {
            csvContent += `"${ld.date}","${ld.checkIn}",${ld.lateMinutes}\n`;
          });
        }
      }

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      const filename = selectedStaffId === 'all' 
        ? `team_attendance_analytics_${new Date().toISOString().slice(0, 10)}.csv`
        : `attendance_analytics_${stats.staffDetail?.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({ title: 'Report exported successfully' });
    } catch (e) {
      toast({ title: 'Failed to export CSV report', variant: 'destructive' });
    }
  };

  // Derived Analytics logic
  const getLateLeaderboard = () => {
    if (!stats || !stats.staffSummary) return [];
    return [...stats.staffSummary]
      .filter(s => s.late > 0)
      .sort((a, b) => b.late - a.late || b.lateMinutes - a.lateMinutes)
      .slice(0, 5);
  };

  const getOvertimeAnalysis = () => {
    if (!stats || !stats.staffSummary) return [];
    return stats.staffSummary.map(s => {
      const staffProfile = staffList.find(sp => sp.id === s.staffId);
      const entry = staffProfile?.entryTime || '09:00';
      const exit = staffProfile?.exitTime || '18:00';
      const [enH, enM] = entry.split(':').map(Number);
      const [exH, exM] = exit.split(':').map(Number);
      const stdHours = exH + (exM / 60) - (enH + (enM / 60));
      
      const expectedHours = s.daysWorked * stdHours;
      const actualHours = s.totalHours;
      const diff = actualHours - expectedHours;
      
      return {
        ...s,
        entry,
        exit,
        stdHours,
        expectedHours,
        actualHours,
        diff
      };
    }).sort((a, b) => b.diff - a.diff);
  };

  // Calendar logic helpers
  const getCalendarDays = () => {
    if (periodType !== 'month') {
      // If custom period is selected, get days from custom start/end range
      const days = [];
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const curr = new Date(start);
      while (curr <= end) {
        days.push(new Date(curr));
        curr.setDate(curr.getDate() + 1);
      }
      return { days, padding: 0 };
    }

    // Month view grid
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const totalDays = lastDay.getDate();
    const startPadding = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    const days = [];
    // Padding days for grid offset
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    // Actual days of month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month - 1, i));
    }
    return { days, padding: startPadding };
  };

  const getDayStatus = (date: Date) => {
    if (!stats || !stats.staffDetail) return { type: 'none', label: 'No Data' };
    const dateStr = getLocalDateString(date);
    const detail = stats.staffDetail;

    // Check if in the future
    const todayStr = getLocalDateString(new Date());
    const isFuture = dateStr > todayStr;

    // Check if before joining date
    const staffCreatedStr = detail.createdAt ? detail.createdAt.split('T')[0] : null;
    if (staffCreatedStr && dateStr < staffCreatedStr) {
      return { type: 'future', label: 'Not Joined Yet', record: null };
    }

    // Check Leaves
    if (detail.leaves && detail.leaves.includes(dateStr)) {
      return { type: 'leave', label: 'Approved Paid Leave', record: null };
    }

    // Check Off Days
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[date.getDay()];
    const isOffDay = dayName === 'Sunday' || (detail.offDays && detail.offDays.includes(dayName));

    // Look up attendance record
    const record = detail.records?.find(r => r.date === dateStr);

    if (record) {
      if (record.status === 'present') return { type: 'present', label: 'Present (On Time)', record };
      if (record.status === 'late') return { type: 'late', label: 'Late Clock In', record };
      if (record.status === 'absent') return { type: 'absent', label: 'Absent', record };
      return { type: 'other', label: record.status, record };
    }

    if (isOffDay) {
      return { type: 'off', label: `Off Day (${dayName.slice(0,3)})`, record: null };
    }

    if (isFuture) {
      return { type: 'future', label: 'Future Day', record: null };
    }

    // Past day with no record is Absent
    return { type: 'absent', label: 'Absent', record: null };
  };

  const lateLeaderboard = getLateLeaderboard();
  const overtimeAnalysis = getOvertimeAnalysis();
  const calendarData = stats?.staffDetail ? getCalendarDays() : null;
  const calendarSelectedDayInfo = selectedCalDate ? getDayStatus(new Date(selectedCalDate)) : null;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-6.5 h-6.5 text-indigo-650 dark:text-indigo-400" />
            Attendance Analytics
          </h1>
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
            Monitor punctuality, working hours trend, and calendar breakdowns.
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {stats && (
            <Button
              onClick={handleExportCSV}
              className="h-10 rounded-xl font-bold bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-350 hover:bg-slate-50 dark:hover:bg-slate-850/60 shadow-sm transition-all flex items-center gap-1.5"
            >
              <Download className="w-4.5 h-4.5" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <GlassCard className="border-slate-200/60 dark:border-slate-800/40 bg-white/70 dark:bg-slate-900/50 shadow-sm rounded-2xl">
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          {/* Period selector type */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-950 p-1 rounded-xl border border-slate-200/40 dark:border-slate-800">
            <button
              onClick={() => setPeriodType('month')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                periodType === 'month'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-750 dark:hover:text-slate-300'
              }`}
            >
              Month-wise
            </button>
            <button
              onClick={() => setPeriodType('custom')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                periodType === 'custom'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-750 dark:hover:text-slate-300'
              }`}
            >
              Custom Range
            </button>
          </div>

          {/* Month selector */}
          {periodType === 'month' && (
            <div className="w-[180px] animate-in fade-in slide-in-from-left-2 duration-200">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="h-9 rounded-xl border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-950 font-bold text-xs text-slate-700 dark:text-slate-300">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
                  {monthOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value} className="text-xs font-semibold">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom Date Inputs */}
          {periodType === 'custom' && (
            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-200">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="h-9 text-xs rounded-xl w-[130px] border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-950 font-semibold"
              />
              <span className="text-xs text-slate-400 font-bold">to</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="h-9 text-xs rounded-xl w-[130px] border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-950 font-semibold"
              />
            </div>
          )}

          {/* Staff Selector */}
          <div className="w-[180px] ml-auto">
            <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
              <SelectTrigger className="h-9 rounded-xl border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-950 font-bold text-xs text-slate-700 dark:text-slate-300">
                <SelectValue placeholder="Select Staff" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
                <SelectItem value="all" className="text-xs font-bold text-slate-700 dark:text-slate-300">All Employees</SelectItem>
                {staffList.map((s) => (
                  <SelectItem key={s.id} value={s.id} className="text-xs font-semibold">
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </GlassCard>

      {/* Main Analytics Content */}
      <AnimatePresence mode="wait">
        {loading && !stats ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <span className="w-9 h-9 border-3 border-indigo-655 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Analytics...</p>
          </div>
        ) : !stats ? (
          <div className="text-center py-24">
            <AlertCircle className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-700 dark:text-slate-300">No Analytics Data</h3>
            <p className="text-xs text-slate-400 font-semibold mt-1">Please select another date range or record check-in logs.</p>
          </div>
        ) : selectedStaffId !== 'all' && stats.staffDetail ? (
          /* ======================================================== */
          /* ================ PER-STAFF ANALYTICS ================== */
          /* ======================================================== */
          <motion.div
            key="staff-analytics"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {/* Header profile details */}
            <motion.div variants={itemVariants} className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-950/20 border border-slate-100 dark:border-slate-800/60 rounded-2xl">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-black text-lg shadow-md shadow-indigo-500/10">
                {stats.staffDetail.name.charAt(0)}
              </div>
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-1.5">
                  {stats.staffDetail.name}
                  <Badge variant="secondary" className="text-[10px] font-bold h-5 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                    Individual View
                  </Badge>
                </h3>
                <p className="text-xs text-slate-400 font-semibold">
                  Shift Timing: {stats.staffDetail.entryTime || '09:00'} - {stats.staffDetail.exitTime || '18:00'}
                </p>
              </div>
            </motion.div>

            {/* Stats Cards Row */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'On Time Shifts', value: stats.staffDetail.totalPresent, icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/10' },
                { label: 'Late Shifts', value: stats.staffDetail.totalLate, icon: AlertCircle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/5 dark:bg-amber-500/10 border-amber-500/10' },
                { label: 'Total Late Hours', value: formatHM(stats.staffDetail.totalLateMinutes), icon: Timer, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/5 dark:bg-rose-500/10 border-rose-500/10' },
                { label: 'Absent Shifts', value: stats.staffDetail.totalAbsent, icon: AlertCircle, color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100/50 dark:bg-slate-900/50 border-slate-200/10' },
              ].map(s => (
                <GlassCard key={s.label} className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                  <CardContent className={`p-4 ${s.bg} rounded-2xl border flex flex-col justify-between h-full`}>
                    <s.icon className={`w-5 h-5 ${s.color} mb-3`} />
                    <div>
                      <p className={`text-2xl font-black ${s.color} leading-none`}>{s.value}</p>
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1.5">{s.label}</p>
                    </div>
                  </CardContent>
                </GlassCard>
              ))}
            </motion.div>

            {/* Attendance Calendar Grid */}
            {calendarData && (
              <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Monthly Calendar View */}
                <GlassCard className="lg:col-span-2 border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40 shadow-sm">
                  <CardHeader className="pb-2 pt-5 px-5 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                      <CalendarIcon className="w-4.5 h-4.5 text-indigo-500" />
                      Attendance Calendar — {periodType === 'month' ? selectedMonth : 'Custom Range'}
                    </CardTitle>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-2 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Present</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Late</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> Absent</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Leave</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-350" /> Off</span>
                    </div>
                  </CardHeader>
                  <CardContent className="px-5 pb-5">
                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-2 text-center text-xs">
                      {/* Weekday headers */}
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(w => (
                        <div key={w} className="font-extrabold text-slate-400 py-1 uppercase text-[10px]">{w}</div>
                      ))}
                      {/* Day cells */}
                      {calendarData.days.map((date, idx) => {
                        if (!date) {
                          return <div key={`pad-${idx}`} className="aspect-square bg-slate-50/10 dark:bg-slate-950/5 rounded-xl border border-transparent" />;
                        }

                        const dayNum = date.getDate();
                        const dateStr = getLocalDateString(date);
                        const { type, label, record } = getDayStatus(date);
                        const isSelected = selectedCalDate === dateStr;

                        let styleClasses = 'bg-white dark:bg-slate-950 border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300';
                        let badgeColor = '';

                        if (type === 'present') {
                          styleClasses = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border-emerald-250';
                          badgeColor = 'bg-emerald-500';
                        } else if (type === 'late') {
                          styleClasses = 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border-amber-250';
                          badgeColor = 'bg-amber-500';
                        } else if (type === 'absent') {
                          styleClasses = 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400 border-rose-250';
                          badgeColor = 'bg-rose-500';
                        } else if (type === 'leave') {
                          styleClasses = 'bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400 border-blue-250';
                          badgeColor = 'bg-blue-500';
                        } else if (type === 'off') {
                          styleClasses = 'bg-slate-100/50 text-slate-400 dark:bg-slate-900/60 dark:text-slate-550 border-slate-200/20';
                          badgeColor = 'bg-slate-300 dark:bg-slate-700';
                        } else if (type === 'future') {
                          styleClasses = 'bg-slate-50/20 text-slate-300 dark:bg-slate-950/20 dark:text-slate-800 border-slate-100/10';
                        }

                        return (
                          <button
                            key={dateStr}
                            onClick={() => setSelectedCalDate(dateStr)}
                            className={`aspect-square rounded-xl border flex flex-col items-center justify-between p-1.5 transition-all hover:scale-105 ${styleClasses} ${
                              isSelected ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-slate-950' : ''
                            }`}
                          >
                            <span className="font-extrabold text-[11px] self-start leading-none">{dayNum}</span>
                            {badgeColor && (
                              <span className={`w-1.5 h-1.5 rounded-full ${badgeColor}`} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </GlassCard>

                {/* Day Details Sidebox */}
                <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40 shadow-sm flex flex-col">
                  <CardHeader className="pb-2 pt-5 px-5">
                    <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200">
                      Day Log Detail
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 flex-grow flex flex-col justify-center">
                    {selectedCalDate && calendarSelectedDayInfo ? (
                      <div className="space-y-4 animate-in fade-in slide-in-from-right-1 duration-200">
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Selected Date</p>
                          <p className="text-base font-extrabold text-slate-800 dark:text-slate-100">
                            {new Date(selectedCalDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>

                        <div className="p-3.5 rounded-xl border border-slate-100 dark:border-slate-850 bg-white/40 dark:bg-slate-950/40 space-y-3.5">
                          <div>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider leading-none">Shift Status</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              {calendarSelectedDayInfo.type === 'present' && <Badge variant="success" className="text-[10px] font-black uppercase">On Time</Badge>}
                              {calendarSelectedDayInfo.type === 'late' && <Badge variant="warning" className="text-[10px] font-black uppercase">Late</Badge>}
                              {calendarSelectedDayInfo.type === 'absent' && <Badge variant="destructive" className="text-[10px] font-black uppercase">Absent</Badge>}
                              {calendarSelectedDayInfo.type === 'leave' && <Badge className="bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-black uppercase">Approved Leave</Badge>}
                              {calendarSelectedDayInfo.type === 'off' && <Badge variant="secondary" className="text-[10px] font-black uppercase">Off Day</Badge>}
                              {calendarSelectedDayInfo.type === 'future' && <Badge variant="secondary" className="text-[10px] font-black uppercase">Upcoming</Badge>}
                              <span className="text-xs font-extrabold text-slate-650 dark:text-slate-350">{calendarSelectedDayInfo.label}</span>
                            </div>
                          </div>

                          {calendarSelectedDayInfo.record && (
                            <div className="grid grid-cols-2 gap-3 text-xs pt-1 border-t border-slate-100 dark:border-slate-800/40">
                              <div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Clock In</p>
                                <p className="font-extrabold text-slate-800 dark:text-slate-200 mt-0.5">
                                  {calendarSelectedDayInfo.record.checkIn 
                                    ? new Date(calendarSelectedDayInfo.record.checkIn).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                                    : '—'
                                  }
                                </p>
                              </div>
                              <div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Clock Out</p>
                                <p className="font-extrabold text-slate-800 dark:text-slate-200 mt-0.5">
                                  {calendarSelectedDayInfo.record.checkOut 
                                    ? new Date(calendarSelectedDayInfo.record.checkOut).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                                    : '—'
                                  }
                                </p>
                              </div>
                              <div className="col-span-2 pt-1 border-t border-slate-100 dark:border-slate-800/40 flex items-center justify-between">
                                <div>
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Working Hours</p>
                                  <p className="font-bold text-slate-700 dark:text-slate-300 mt-0.5">
                                    {calendarSelectedDayInfo.record.workingHours 
                                      ? formatHM(calendarSelectedDayInfo.record.workingHours * 60)
                                      : '00H 00M'
                                    }
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Status</p>
                                  <Badge className={`text-[10px] font-bold mt-0.5 uppercase ${
                                    calendarSelectedDayInfo.record.verification === 'verified'
                                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-250/20'
                                      : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-255/20'
                                  }`}>
                                    {calendarSelectedDayInfo.record.verification || 'pending'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-400 font-semibold">
                        <CalendarIcon className="w-10 h-10 text-slate-300 dark:text-slate-750 mx-auto mb-2" />
                        <p className="text-xs">Click on any day in the calendar grid to view detailed timings and shift information.</p>
                      </div>
                    )}
                  </CardContent>
                </GlassCard>
              </motion.div>
            )}

            {/* Salary details and earnings cards */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { label: 'Wallet Balance', value: `₹${stats.staffDetail.walletBalance.toLocaleString('en-IN')}`, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/5 dark:bg-emerald-500/10' },
                { label: 'Configured Wages', value: `₹${stats.staffDetail.totalWages.toLocaleString('en-IN')}`, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/5 dark:bg-amber-500/10' },
                { label: 'Total Hours Worked', value: formatHM(stats.staffDetail.totalWorkingHours * 60), color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500/5 dark:bg-violet-500/10' },
                { label: 'Average Daily Hours', value: formatHM(stats.staffDetail.avgWorkingHours * 60), color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-500/5 dark:bg-indigo-500/10' },
                { label: 'Overtime Extra Hours', value: formatHM((stats.staffDetail.totalExtraHours || 0) * 60), color: 'text-fuchsia-600 dark:text-fuchsia-400', bg: 'bg-fuchsia-500/5 dark:bg-fuchsia-500/10' },
              ].map(s => (
                <GlassCard key={s.label} className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                  <CardContent className={`p-4 ${s.bg} rounded-2xl`}>
                    <p className="text-xl font-black text-slate-850 dark:text-slate-100 leading-none">{s.value}</p>
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1.5">{s.label}</p>
                  </CardContent>
                </GlassCard>
              ))}
            </motion.div>

            {/* Monthly Salary Breakdown card */}
            <motion.div variants={itemVariants}>
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40 shadow-sm">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                    <IndianRupee className="w-4.5 h-4.5 text-emerald-500" />
                    Monthly Wages breakdown — {stats.staffDetail.currentMonth}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-3.5 rounded-xl bg-slate-100/50 dark:bg-slate-900/50 border border-slate-200/5 dark:border-slate-800/50">
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Days in Month</p>
                      <p className="text-xl font-black text-slate-900 dark:text-white mt-1">{stats.staffDetail.daysInMonth}</p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-rose-500/5 dark:bg-rose-500/10 border border-rose-500/10">
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Off Days</p>
                      <p className="text-xl font-black text-rose-600 dark:text-rose-455 mt-1">{stats.staffDetail.offDaysInMonth}</p>
                      <p className="text-[9px] text-slate-400 font-semibold mt-0.5">
                        {stats.staffDetail.offDays.length > 0 ? stats.staffDetail.offDays.map(d => d.slice(0, 3)).join(', ') : 'None'}
                      </p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/10">
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Working Days</p>
                      <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{stats.staffDetail.workingDaysInMonth}</p>
                      <p className="text-[9px] text-slate-400 font-semibold mt-0.5">{stats.staffDetail.daysInMonth} - {stats.staffDetail.offDaysInMonth}</p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/10">
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Configured Wages</p>
                      <p className="text-xl font-black text-amber-600 dark:text-amber-400 mt-1">₹{stats.staffDetail.totalWages.toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/5 to-blue-500/5 dark:from-emerald-500/10 dark:to-blue-500/10 border border-emerald-250/20 dark:border-emerald-800/20">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Daily Wage Rate</p>
                        <p className="text-lg font-black text-slate-900 dark:text-white mt-1">₹{stats.staffDetail.expectedMonthlySalary.toLocaleString('en-IN')}</p>
                        <p className="text-[9px] text-slate-400 font-semibold">₹{stats.staffDetail.totalWages} ÷ {stats.staffDetail.workingDaysInMonth} days</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Paid Days Worked</p>
                        <p className="text-lg font-black text-blue-600 dark:text-blue-400 mt-1">{stats.staffDetail.daysPaid}</p>
                        <p className="text-[9px] text-slate-400 font-semibold">Completed shifts + paid leaves</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Earned Wages</p>
                        <p className="text-lg font-black text-emerald-600 dark:text-emerald-450 mt-1">₹{stats.staffDetail.earnedSalary.toLocaleString('en-IN')}</p>
                        <p className="text-[9px] text-slate-400 font-semibold">Daily rate × {stats.staffDetail.daysPaid} days</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Wages Deduction</p>
                        <p className={`text-lg font-black mt-1 ${stats.staffDetail.salaryDeduction > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {stats.staffDetail.salaryDeduction > 0 ? `-₹${stats.staffDetail.salaryDeduction.toLocaleString('en-IN')}` : '₹0'}
                        </p>
                        <p className="text-[9px] text-slate-400 font-semibold">{stats.staffDetail.totalAbsent} absent days</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Individual Charts Grid */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Late arrivals */}
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-amber-500" />
                    Late Arrival Minutes Trend
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.staffDetail.lateOverTime}>
                        <defs>
                          <linearGradient id="sLateGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = new Date(label + 'T00:00:00');
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl p-2.5 shadow-xl text-[11px] font-semibold">
                              <p className="text-slate-700 dark:text-slate-350">{d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                              <p className="text-amber-600 mt-1">Late Time: {formatHM((payload[0].payload as any).lateMinutes)}</p>
                            </div>
                          );
                        }} />
                        <Area type="monotone" dataKey="late" stroke="#f59e0b" strokeWidth={2.5} fill="url(#sLateGrad)" dot={false} activeDot={{ r: 5, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>

              {/* Heatmap checkins */}
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Fingerprint className="w-4 h-4 text-violet-500" />
                    Check-in Hour Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.staffDetail.checkInDistribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                        <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(h) => `${h}:00`} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} allowDecimals={false} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl p-2.5 shadow-xl text-[11px] font-semibold">
                              <p className="text-slate-700 dark:text-slate-350">{label}:00 - {(label as number) + 1}:00</p>
                              <p className="text-violet-600 mt-1">{payload[0].value} check-ins</p>
                            </div>
                          );
                        }} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]} maxBarSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Working Hours Trend */}
            <motion.div variants={itemVariants}>
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                    <Clock className="w-4.5 h-4.5 text-blue-500" />
                    Working Hours Trend & Stability
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.staffDetail.workingHoursTrend}>
                        <defs>
                          <linearGradient id="sHoursGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} domain={[0, 'auto']} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = new Date(label + 'T00:00:00');
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl p-2.5 shadow-xl text-[11px] font-semibold">
                              <p className="text-slate-700 dark:text-slate-355">{d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                              <p className="text-blue-600 mt-1">{formatHM(payload[0].value * 60)} worked</p>
                            </div>
                          );
                        }} />
                        <Area type="monotone" dataKey="avgHours" stroke="#3b82f6" strokeWidth={2.5} fill="url(#sHoursGrad)" dot={false} activeDot={{ r: 5, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Late details table list */}
            {stats.staffDetail.lateDetails.length > 0 && (
              <motion.div variants={itemVariants}>
                <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40 shadow-sm">
                  <CardHeader className="pb-2 pt-5 px-5">
                    <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                      <Timer className="w-4.5 h-4.5 text-rose-500" />
                      Late Arrivals Detail Log ({stats.staffDetail.lateDetails.length} instances)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-5">
                    <div className="max-h-[280px] overflow-y-auto rounded-xl border border-slate-100 dark:border-slate-800/60">
                      <Table>
                        <TableHeader className="bg-slate-50 dark:bg-slate-950 font-semibold text-xs">
                          <TableRow>
                            <TableHead className="font-bold text-slate-600 dark:text-slate-400">Date</TableHead>
                            <TableHead className="font-bold text-slate-600 dark:text-slate-400">Check-in Time</TableHead>
                            <TableHead className="font-bold text-slate-600 dark:text-slate-400 text-right">Delay Time</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stats.staffDetail.lateDetails.map((ld, i) => (
                            <TableRow key={i} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/20 text-xs">
                              <TableCell className="font-semibold text-slate-700 dark:text-slate-350">
                                {new Date(ld.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                              </TableCell>
                              <TableCell className="font-semibold text-slate-500 dark:text-slate-450">{ld.checkIn} IST</TableCell>
                              <TableCell className="text-right">
                                <Badge className="bg-rose-500/5 text-rose-600 dark:text-rose-455 border border-rose-200/10 font-bold rounded-lg text-[10px] px-2 py-0.5">
                                  +{formatHM(ld.lateMinutes)} late
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </GlassCard>
              </motion.div>
            )}
          </motion.div>
        ) : (
          /* ======================================================== */
          /* ================== TEAM-WIDE ANALYTICS ================== */
          /* ======================================================== */
          <motion.div
            key="team-analytics"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {/* Quick Metrics Cards */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Team On-Time Count', value: `${stats.statusBreakdown.present} days`, icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/10' },
                { label: 'Team Late Shifts', value: `${stats.statusBreakdown.late} days`, icon: AlertCircle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/5 dark:bg-amber-500/10 border-amber-500/10' },
                { label: 'Team Absent Days', value: `${stats.statusBreakdown.absent} days`, icon: AlertCircle, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/5 dark:bg-rose-500/10 border-rose-500/10' },
                { label: 'Total Logs Count', value: `${stats.statusBreakdown.present + stats.statusBreakdown.late} shifts`, icon: Users, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-500/5 dark:bg-indigo-500/10 border-indigo-500/10' },
              ].map(s => (
                <GlassCard key={s.label} className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                  <CardContent className={`p-4 ${s.bg} rounded-2xl border flex flex-col justify-between h-full`}>
                    <s.icon className={`w-5 h-5 ${s.color} mb-3`} />
                    <div>
                      <p className={`text-2xl font-black ${s.color} leading-none`}>{s.value}</p>
                      <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1.5">{s.label}</p>
                    </div>
                  </CardContent>
                </GlassCard>
              ))}
            </motion.div>

            {/* Leaderboards and Special Lists */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Frequently Late Leaderboard */}
              <GlassCard className="lg:col-span-1 border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                    <TrendingDown className="w-4.5 h-4.5 text-amber-500" />
                    Frequent Latecomers
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="space-y-3.5">
                    {lateLeaderboard.map((item, idx) => (
                      <div key={item.staffId} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/60 pb-2.5 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black text-slate-450 bg-slate-100 dark:bg-slate-800 dark:text-slate-500 rounded-full w-5 h-5 flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-sm font-extrabold text-slate-850 dark:text-slate-200">{item.name}</p>
                            <p className="text-[10px] text-slate-400 font-semibold">{item.late} late shifts ({formatHM(item.lateMinutes)})</p>
                          </div>
                        </div>
                        <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold text-[10px] px-2 py-0.5 border border-amber-250/20">
                          {item.late} Instances
                        </Badge>
                      </div>
                    ))}
                    {lateLeaderboard.length === 0 && (
                      <div className="text-center py-8 text-xs text-slate-400 font-semibold">
                        <Sparkles className="w-8 h-8 text-emerald-500 mx-auto mb-2 animate-bounce" />
                        Wow! No late arrivals this period.
                      </div>
                    )}
                  </div>
                </CardContent>
              </GlassCard>

              {/* Status breakdown pie chart */}
              <GlassCard className="lg:col-span-2 border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-800 dark:text-slate-200">Attendance Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'On Time', value: stats.statusBreakdown.present, fill: '#10b981' },
                            { name: 'Late', value: stats.statusBreakdown.late, fill: '#f59e0b' },
                            { name: 'Absent', value: stats.statusBreakdown.absent, fill: '#f43f5e' },
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={4}
                          dataKey="value"
                          stroke="none"
                        >
                          <Cell key="cell-0" fill="#10b981" />
                          <Cell key="cell-1" fill="#f59e0b" />
                          <Cell key="cell-2" fill="#f43f5e" />
                        </Pie>
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 shadow-xl text-xs">
                              <p className="font-bold" style={{ color: payload[0].payload.fill }}>{payload[0].name}</p>
                              <p className="text-slate-650 dark:text-slate-400 font-bold mt-1">{payload[0].value} days</p>
                            </div>
                          );
                        }} />
                        <Legend verticalAlign="bottom" height={36} formatter={(value) => <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{value}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Row 2: Overtime Trend + Heatmap */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Overtime trends */}
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-805 dark:text-slate-200 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-amber-500" />
                    Late Arrivals Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.lateOverTime}>
                        <defs>
                          <linearGradient id="lateGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} allowDecimals={false} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = new Date(label + 'T00:00:00');
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 shadow-xl text-xs">
                              <p className="font-bold text-slate-750 dark:text-slate-350">{d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                              <p className="text-amber-600 font-semibold mt-1">Late count: {payload[0].value}</p>
                            </div>
                          );
                        }} />
                        <Area type="monotone" dataKey="late" stroke="#f59e0b" strokeWidth={2.5} fill="url(#lateGrad)" dot={false} activeDot={{ r: 5, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>

              {/* Heatmap Checkins */}
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2">
                    <Fingerprint className="w-4 h-4 text-violet-500" />
                    Check-in Time Heatmap
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.checkInDistribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                        <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(h) => `${h}:00`} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} allowDecimals={false} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 shadow-xl text-xs">
                              <p className="font-bold text-slate-700 dark:text-slate-350">{label}:00 - {(label as number) + 1}:00</p>
                              <p className="text-violet-605 font-semibold mt-1">{payload[0].value} check-ins</p>
                            </div>
                          );
                        }} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]} maxBarSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Team working hours trend */}
            <motion.div variants={itemVariants}>
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    Avg Working Hours Trend (Team)
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.workingHoursTrend}>
                        <defs>
                          <linearGradient id="hoursGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} />
                        <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} domain={[0, 'auto']} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = new Date(label + 'T00:00:00');
                          return (
                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 shadow-xl text-xs">
                              <p className="font-bold text-slate-700 dark:text-slate-355">{d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                              <p className="text-blue-600 mt-1">{formatHM(payload[0].value * 60)} avg hours</p>
                            </div>
                          );
                        }} />
                        <Area type="monotone" dataKey="avgHours" stroke="#3b82f6" strokeWidth={2.5} fill="url(#hoursGrad)" dot={false} activeDot={{ r: 5, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Overtime vs Standard Shift Hours analysis table */}
            <motion.div variants={itemVariants}>
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40 shadow-sm">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-855 dark:text-slate-200 flex items-center gap-2">
                    <Award className="w-4.5 h-4.5 text-indigo-500" />
                    Overtime & Under-hours analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="rounded-xl border border-slate-100 dark:border-slate-850/65 overflow-hidden">
                    <Table>
                      <TableHeader className="bg-slate-50 dark:bg-slate-950 font-semibold text-xs">
                        <TableRow>
                          <TableHead className="font-bold text-slate-650 dark:text-slate-400">Staff Profile</TableHead>
                          <TableHead className="font-bold text-slate-650 dark:text-slate-400 text-center">Shift Timing</TableHead>
                          <TableHead className="font-bold text-slate-650 dark:text-slate-400 text-center">Days Worked</TableHead>
                          <TableHead className="font-bold text-slate-650 dark:text-slate-400 text-center">Expected Hours</TableHead>
                          <TableHead className="font-bold text-slate-655 dark:text-slate-400 text-center">Actual Hours</TableHead>
                          <TableHead className="font-bold text-slate-650 dark:text-slate-400 text-right">Hours Difference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overtimeAnalysis.map((s) => {
                          const isPositive = s.diff >= 0;
                          return (
                            <TableRow key={s.staffId} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/20 text-xs">
                              <TableCell className="font-extrabold text-slate-800 dark:text-slate-200">{s.name}</TableCell>
                              <TableCell className="text-center font-semibold text-slate-550 dark:text-slate-400">{s.entry} - {s.exit}</TableCell>
                              <TableCell className="text-center font-bold text-slate-700 dark:text-slate-300">{s.daysWorked}</TableCell>
                              <TableCell className="text-center font-semibold text-slate-500 dark:text-slate-455">{formatHM(s.expectedHours * 60)}</TableCell>
                              <TableCell className="text-center font-bold text-slate-700 dark:text-slate-350">{formatHM(s.actualHours * 60)}</TableCell>
                              <TableCell className="text-right">
                                <Badge className={`font-bold rounded-lg text-[10px] px-2 py-0.5 ${
                                  isPositive 
                                    ? 'bg-emerald-500/5 text-emerald-600 dark:text-emerald-450 border border-emerald-205/10'
                                    : 'bg-rose-500/5 text-rose-600 dark:text-rose-455 border border-rose-205/10'
                                }`}>
                                  {isPositive ? '+' : ''}{formatHM(s.diff * 60)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>

            {/* Staff-wise Progress comparison list */}
            <motion.div variants={itemVariants}>
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40">
                <CardHeader className="pb-2 pt-5 px-5">
                  <CardTitle className="text-sm font-bold text-slate-855 dark:text-slate-200 flex items-center gap-2">
                    <Users className="w-4 h-4 text-emerald-500" />
                    Staff-wise Attendance Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="space-y-4">
                    {stats.staffSummary.map((s) => {
                      const total = s.present + s.late + s.absent;
                      const pPct = total > 0 ? Math.round((s.present / total) * 100) : 0;
                      const lPct = total > 0 ? Math.round((s.late / total) * 100) : 0;
                      const aPct = total > 0 ? Math.round((s.absent / total) * 100) : 0;
                      return (
                        <div key={s.staffId} className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{s.name}</span>
                            <div className="flex items-center gap-3 text-[10px] font-bold">
                              <span className="text-emerald-600 dark:text-emerald-400">{s.present} On Time</span>
                              <span className="text-amber-600 dark:text-amber-400">{s.late} Late</span>
                              <span className="text-rose-600 dark:text-rose-455">{s.absent} Absent</span>
                            </div>
                          </div>
                          <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
                            <div className="bg-emerald-500 transition-all" style={{ width: `${pPct}%` }} />
                            <div className="bg-amber-500 transition-all" style={{ width: `${lPct}%` }} />
                            <div className="bg-rose-500 transition-all" style={{ width: `${aPct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {stats.staffSummary.length === 0 && (
                      <p className="text-center text-sm text-slate-400 py-8">No staff data available for this period.</p>
                    )}
                  </div>
                </CardContent>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAttendanceAnalyticsPage;
